<template>
	<div class="page-content mt--2">
		<ja-dashboard-container
			class="mb--2"
			tabable
			:active-tab="visibleTab"
			:tab-options="tabOptions"
			@selectedTab="updateTabVisibility"
		>
			<content v-if="visibleTab === 'passcodes'">
				<div class="panel mb--2">
					<div class="panel__title">
						{{ $t('simPasscodesTitle') }}
					</div>
					<div class="panel__content">
						<ja-table
							:use-loader="showLoader"
							:options="{
								columnKeys: ['location', 'passcode']
							}"
							translation-prefix="tables"
							:items="simPasscodes"
						></ja-table>
					</div>
				</div>

				<div class="panel">
					<div class="panel__title">
						{{ $t('categoriesPasscodesTitle') }}
					</div>
					<div class="panel__content">
						<ja-table
							:use-loader="showLoader"
							:options="{ columnKeys: ['category', 'passcode'] }"
							translation-prefix="tables"
							:items="categoryPasscodes"
						></ja-table>
					</div>
				</div>
			</content>
			<content v-if="visibleTab === 'variables'">
				<div class="panel">
					<div class="panel__content mb--2">
						<div class="form mt--2 align-items--end">
							<ja-button
								name="update"
								class="small"
								@click.native="openAreaSettingsModal"
							>
								{{ $t('edit') }}
							</ja-button>
						</div>
					</div>
				</div>
				<div class="panel">
					<div class="panel__content">
						<ja-table
							:use-loader="showLoader"
							:options="{ columnKeys: ['variable', 'value'] }"
							translation-prefix="tables"
							:items="areaSettings"
						>
						</ja-table>
					</div>
				</div>
			</content>
		</ja-dashboard-container>
		<ja-confirm-modal
			class="modal--large"
			:title="modalTitle"
			confirm-text="Save"
			:show="isModalVisible"
			@closeModal="closeAreaSettingsModal"
			@cancel="closeAreaSettingsModal"
			@confirm="updateAreaSettings"
		>
			<div class="form form-col--2">
				<div
					v-for="(setting, index) in areaSettings"
					:key="index"
					class="form-control"
				>
					<ja-input
						v-model="settingForUpdate[setting.key]"
						:name="setting.key"
						input-type="number"
						:label="setting.variable"
					></ja-input>
				</div>
			</div>
		</ja-confirm-modal>
	</div>
</template>

<script>
import DashboardContainer from '../elements/DashboardContainer';
import gql from 'graphql-tag';
import Table from '../../ui/Table.vue';
import { uniqBy, orderBy } from 'lodash';
import { Input } from '@/components/ui/form';
import Button from '@/components/ui/Button';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import SettingsService from '@/services/SettingsService';
import FacilitiesService from '@/services/FacilitiesService';
import AdminFacility from '@/mixins/AdminFacility';

export default {
	components: {
		jaDashboardContainer: DashboardContainer,
		jaTable: Table,
		jaButton: Button,
		jaConfirmModal: ConfirmModal,
		jaInput: Input
	},
	mixins: [AdminFacility],
	data() {
		return {
			tabOptions: [
				{
					title: this.$t('passcodes'),
					id: 'passcodes'
				},
				{
					title: this.$t('variables'),
					id: 'variables'
				}
			],
			visibleTab: 'passcodes',
			simulationCodes: [],
			categoriesCodes: [],
			areaSettings: [],
			selectedArea: 0,
			selectedSetting: null,
			showLoader: true,
			areas: [],
			isModalVisible: false,
			settingForUpdate: null
		};
	},
	computed: {
		simPasscodes() {
			return this.simulationCodes;
		},
		categoryPasscodes() {
			return orderBy(this.categoriesCodes, 'category');
		},
		modalTitle() {
			return this.selectedSetting ? `Area ${this.selectedSetting.area}` : '';
		},
		selectedFacilityArea() {
			if (this.selectedFacility) {
				const facilityName = this.selectedFacility.name;
				var parts = facilityName.split(' ');

				if (parts.length > 1) {
					const areaCodeAsString = parts[0].trim();
					const areaCode = parseInt(areaCodeAsString);
					if (!isNaN(areaCode)) {
						return areaCode;
					}
				}
			}
			return null;
		}
	},
	watch: {
		// TODO: Future enhancement
		// Add logic to fetch only what is needed
		// based on selected tab
		selectedFacility() {
			if (this.selectedFacilityArea != null) {
				if (this.visibleTab === 'variables') {
					this.fetchVariables();
				} else {
					this.fetchPasscodes();
				}
			}
		},
		visibleTab(value) {
			// since we do not have any changes in passcodes tab
			// this is enough. Have to be updated when Updates are possible
			// for passcodes
			if (value === 'variables') {
				this.fetchVariables();
			} else {
				this.fetchPasscodes();
			}
		}
	},
	async mounted() {
		if (this.userData && !this.isAdmin) {
			this.tabOptions = this.tabOptions.filter(tab => tab.id !== 'variables');
		}

		if (this.selectedFacilityArea != null) {
			await this.fetchPasscodes();
		}
		// TODO: Future enhancement
	},
	methods: {
		convertInterestToPercentage(interest) {
			return interest < 1 ? interest * 100 : interest;
		},
		updateTabVisibility(tabId) {
			this.visibleTab = tabId;
		},
		async fetchPasscodes() {
			// TODO: Move this to a service
			const filters = [];

			// TODO: Future enhancement - Update this to work directly
			// with the filters
			// For now it is all "static" without option for update
			filters.push(`area="${this.selectedFacilityArea || ''}"`);

			const { data } = await this.$apollo.query({
				query: gql`
					query passcodes($where: String!) {
						areaSimulationStagePasscodes(where: $where, take: 1) {
							afterAllocateSavingsPasscode
							afterLifeScenarioPasscode
							afterSetBudgetPasscode
							afterLifeStageSummaryPasscode
							afterCalculateNMIPasscode
							afterLifeStageIntroPasscode
							area
						}
						expenseCategoryAreaPasscodes(where: $where) {
							cmsExpenseCategory {
								name
								cmsExpenseCategoryGroup {
									facility_ID
								}
							}
							passcode
							area
						}
					}
				`,
				variables: {
					where: filters.join('&&')
				}
			});
			this.categoriesCodes = uniqBy(
				data.expenseCategoryAreaPasscodes
					.filter(
						i =>
							i.cmsExpenseCategory !== null &&
							i.cmsExpenseCategory.cmsExpenseCategoryGroup.facility_ID ==
								this.selectedFacilityId
					)
					.map(i => {
						return {
							...i,
							category: i.cmsExpenseCategory.name
						};
					}),
				'category'
			);
			this.simulationCodes = [];
			data.areaSimulationStagePasscodes.forEach(i => {
				this.simulationCodes.push(
					{
						location: this.$t(
							'simulationPasscodeLocations.afterSetBudgetPasscode'
						),
						passcode: i.afterSetBudgetPasscode
					},
					{
						location: this.$t(
							'simulationPasscodeLocations.afterLifeScenarioPasscode'
						),
						passcode: i.afterLifeScenarioPasscode
					},
					{
						location: this.$t(
							'simulationPasscodeLocations.afterLifeStageSummaryPasscode'
						),
						passcode: i.afterLifeStageSummaryPasscode
					},
					{
						location: this.$t(
							'simulationPasscodeLocations.afterLifeStageIntroPasscode'
						),
						passcode: i.afterLifeStageIntroPasscode
					},
					{
						location: this.$t(
							'simulationPasscodeLocations.afterCalculateNMIPasscode'
						),
						passcode: i.afterCalculateNMIPasscode
					}
				);
			});

			if (!this.simulationCodes.length || !this.categoriesCodes.length) {
				this.showLoader = false;
			}
		},
		async fetchVariables() {
			this.areaSettings = [];

			const areaSetting = await SettingsService.fetchVariables(
				this.selectedFacilityArea
			);
			let areaSettingFormat = { ...areaSetting };

			const matchingkeys = [
				'autoLoanInterest',
				'autoLoanCoefficient',
				'autoLoanCreditScoreThreshold',
				'autoLoanDealershipInterest',
				'autoLoanDealershipCoefficient',
				'autoLoanDealershipCreditScoreThreshold',
				'mortgageLoanCreditScoreThreshold',
				'mortgageLoanHighSaverCoefficient',
				'mortgageLoanLowSaverCoefficient',
				'mortgageLoanInterest'
			];
			Object.keys(areaSetting).forEach(key => {
				if (!matchingkeys.includes(key)) {
					return;
				}
				if (
					key === 'autoLoanDealershipInterest' ||
					key === 'mortgageLoanInterest' ||
					key === 'autoLoanInterest'
				) {
					areaSettingFormat[key] = this.convertInterestToPercentage(
						areaSetting[key]
					);

					this.areaSettings.push({
						key: key,
						variable: this.$t(`tables.variables.${key}`),
						value: this.convertInterestToPercentage(areaSetting[key])
					});
				} else {
					// Used in table
					this.areaSettings.push({
						key: key,
						variable: this.$t(`tables.variables.${key}`),
						value: areaSetting[key]
					});
				}
			});

			this.settingForUpdate = areaSettingFormat;
			this.selectedSetting = areaSetting;
		},
		async fetchAreas() {
			this.areas = await FacilitiesService.getUniqueAreas();
			return;
		},
		openAreaSettingsModal() {
			this.isModalVisible = true;
		},
		closeAreaSettingsModal() {
			this.isModalVisible = false;
			// reset object that is used for v-model
			this.settingForUpdate = { ...this.selectedSetting };
		},
		async updateAreaSettings() {
			const updatesOnly = {
				// this is required parameter
				id: this.settingForUpdate.id
			};

			Object.keys(this.settingForUpdate).forEach(key => {
				// check as string because input fields returns a string
				// representation of the value
				if (
					this.settingForUpdate[key].toString() !==
					this.selectedSetting[key].toString()
				) {
					// Parse float is used because some values are with float point
					// but others are just integers
					// INFO: this have to be updated if there is a settings with
					// different value than numeric
					updatesOnly[key] = parseFloat(this.settingForUpdate[key]);
				}
			});

			await SettingsService.updateAreaSettings(updatesOnly);

			this.closeAreaSettingsModal();

			await this.fetchVariables();
		}
	}
};
</script>

<style lang="scss" scoped>
.panel {
	&__title {
		padding: 24px;
		background-color: rgba(200, 207, 227, 0.5);
	}
}
</style>
